import {
  sortOrderMarketingTypes,
  type SortOrder,
} from '@/lib/markup-sort/constants'

import { CONTENT_TYPE_ORIGINS } from '@/lib/enums-marketing'
import contentCollectionService from '@/services/content-collection-service'
import type { ContentCollection } from '@/services/content-collection-service/types'
import { getContentOwnersFilter } from '@/services/filter-markup-service'

export const FILTER_TYPES = Object.freeze({
  MARKUP_ATTR: 'markup attribute',
  VISIBLE_MAP_CONTENT: 'visible map content',
  TEXT_SEARCH: 'text search',
})

type ContentTypeOrigin =
  (typeof CONTENT_TYPE_ORIGINS)[keyof typeof CONTENT_TYPE_ORIGINS]

/**
 * Gets the content type list origin based on the route the user navigated from.
 */
export const getContentTypeListOrigin = (
  fromRoute?: string
): ContentTypeOrigin => {
  const app = 'fish'

  if (fromRoute?.startsWith('content-select')) {
    return CONTENT_TYPE_ORIGINS.LIST_SELECT
  }

  switch (fromRoute) {
    case `my-content-card-${app}`:
      return CONTENT_TYPE_ORIGINS.MY_CONTENT
    case `${app}-my-content-created`:
      return CONTENT_TYPE_ORIGINS.MY_CONTENT
    case `all-markups-card-${app}`:
      return CONTENT_TYPE_ORIGINS.ALL_CONTENT
    case `markup-type-list-card-waypoints-${app}`:
      return CONTENT_TYPE_ORIGINS.WAYPOINT_LIST
    case `markup-type-list-card-tracks-${app}`:
      return CONTENT_TYPE_ORIGINS.TRACK_LIST
    case `markup-type-list-card-lines-${app}`:
      return CONTENT_TYPE_ORIGINS.LINE_LIST
    case `markup-type-list-card-areas-${app}`:
      return CONTENT_TYPE_ORIGINS.SHAPE_LIST
    case `markup-type-list-card-routes-${app}`:
      return CONTENT_TYPE_ORIGINS.ROUTE_LIST
    case `${app}-waypoint`:
      return CONTENT_TYPE_ORIGINS.WAYPOINT_VIEW
    case `${app}-track`:
      return CONTENT_TYPE_ORIGINS.TRACK_VIEW
    case `${app}-line`:
      return CONTENT_TYPE_ORIGINS.LINE_VIEW
    case `${app}-shape`:
      return CONTENT_TYPE_ORIGINS.SHAPE_VIEW
    case `${app}-route`:
      return CONTENT_TYPE_ORIGINS.ROUTE_VIEW
    case `filter-content-${app}`:
      return CONTENT_TYPE_ORIGINS.FILTER_CARD
    default:
      return CONTENT_TYPE_ORIGINS.DIRECT_LINK
  }
}

/**
 * Get the sort type marketing event based upon current sortType value
 * @param sortOrder - sort order string constant
 * @returns sort event value
 */
export const getSortTypeEventValue = (sortOrder: SortOrder): string => {
  return sortOrderMarketingTypes[sortOrder]
}

interface ContentFilters {
  hasPhotos?: boolean
  contentOwners?: string[]
  onlyOnMap?: boolean
  waypointTypes?: string[]
  waypointColors?: string[]
}

/**
 * Get the filters event value from the current filters set on a content list card
 * @param currentFilters - active content filters
 * @returns comma separated string of current filters
 */
export const getFilterTypeListValue = (
  currentFilters: ContentFilters
): string => {
  const filterTypes: string[] = []

  if (currentFilters.hasPhotos) {
    filterTypes.push('items with photos')
  }
  if (currentFilters.contentOwners && currentFilters.contentOwners?.length) {
    filterTypes.push(
      currentFilters.contentOwners.length === 1
        ? 'share by one user'
        : 'share by all users'
    )
  }
  if (currentFilters.onlyOnMap) {
    filterTypes.push('visible map content')
  }
  if (currentFilters.waypointTypes?.length) {
    filterTypes.push('waypoint type')
  }
  if (currentFilters.waypointColors?.length) {
    filterTypes.push('waypoint color')
  }

  return filterTypes.join(', ')
}

/**
 * Gets the permission type for a content collection folder.
 */
export const getFolderPermissionType = async (
  collectionID: string
): Promise<string> => {
  if (contentCollectionService.getAllCollections().length === 0) {
    await contentCollectionService.requestCollections()
  }

  const collection = contentCollectionService.getCollectionByID(
    collectionID
  ) as ContentCollection | undefined

  if (!collection) {
    return ''
  }

  if (collection.permissions?.includes('collection.delete')) {
    return 'owner'
  } else if (collection.permissions?.includes('collection.add_entity')) {
    return 'contributor'
  }

  return 'viewer'
}

interface ContentAddedParams {
  collectionID: string
  changedMarkups?: Array<{ type: string }>
}

/**
 * Builds event data for when content is added to a content collection.
 */
export const getContentCollectionContentAddedData = async ({
  collectionID,
  changedMarkups = [],
}: ContentAddedParams) => {
  const collection = contentCollectionService.getCollectionByID(
    collectionID
  ) as ContentCollection | undefined
  const permissionType = await getFolderPermissionType(collectionID)
  const changedMarkupsCount = changedMarkups.length

  return {
    content_collection_id: collectionID,
    entity_count: changedMarkupsCount,
    waypoint_count:
      changedMarkups.filter((item) => item.type === 'waypoint')?.length ?? 0,
    line_count:
      changedMarkups.filter((item) => item.type === 'line')?.length ?? 0,
    area_count:
      changedMarkups.filter((item) => item.type === 'shape')?.length ?? 0,
    track_count:
      changedMarkups.filter((item) => item.type === 'track')?.length ?? 0,
    route_count:
      changedMarkups.filter((item) => item.type === 'route')?.length ?? 0,
    total_entity_count: collection?.entities.length ?? 0,
    contributor_permission: permissionType,
  }
}

interface ContentRemovedParams {
  collectionID: string
  contentType: string
}

/**
 * Builds event data for when content is removed from a content collection.
 */
export const getContentCollectionContentRemovedData = async ({
  collectionID,
  contentType,
}: ContentRemovedParams) => {
  const collection = contentCollectionService.getCollectionByID(
    collectionID
  ) as ContentCollection | undefined
  const permissionType = await getFolderPermissionType(collectionID)

  return {
    content_collection_id: collectionID,
    entity_count: 1,
    waypoint_count: contentType === 'waypoint' ? 1 : 0,
    line_count: contentType === 'line' ? 1 : 0,
    area_count: contentType === 'shape' ? 1 : 0,
    track_count: contentType === 'track' ? 1 : 0,
    route_count: contentType === 'route' ? 1 : 0,
    total_entity_count: collection?.entities.length ?? 0,
    contributor_permission: permissionType,
  }
}

/**
 * Gets value for shared_by event property
 * @returns value for shared_by event property
 */
export const getSharedBy = (): string | null => {
  const nrOfContentOwners = getContentOwnersFilter().length
  if (nrOfContentOwners === 1) {
    return 'one user'
  } else if (nrOfContentOwners > 1) {
    return 'multiple users'
  }
  return null
}
